import { Copy } from "@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  ASSET_SCOPE1_CI,
  AVG_CARBON_INT,
  EQUITY_SCOPE,
  EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION,
  KPI_NCI_IMPACT,
  MARGIN_TO_CARBON_RATIO_AVG,
  MCR_AVG_UOM,
  MTCO2E,
  NATURAL_GAS,
  NET_ABSOLUTE_EMISSIONS,
  NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION,
  OPERATED_SCOPE,
  SCOPE3_CAT11_EMISSION,
  SLMT_CONVERSION_FACTORS,
  TABLE_ROW_TOTAL_VOLUME,
  TS_PRODUCTS_CAT11_FACTORS,
  VOLUME_WEIGHTED_AVG_CARBON_INT,
} from "../../api/constants";
import {
  PointTwoNineThree,
  ThreePointSix,
  five,
  four,
  pointnine,
  six,
  tenlakh,
  thousand,
  thousandfivehundred,
  three,
  two,
} from "../../constants";
import { MJ_PER_KBBL } from "../../constants/conversionFactors";
import type {
  ICalculatorFormState,
  ICarbonIntensityForSLMT,
  IGroupNCI,
  IKpiCardData,
  IKpiContentData,
  IKpiModal,
  IKpiTableData,
  IProductCarbonIntensity,
  IRecord,
  IStepTwoTableData,
  Idispatch,
  Iparameters,
  IyearWiseKPIrecord,
  YearWiseData,
} from "../../types/types";
import { convertBigIntToDecimal, convertInputToCommaSeparatedWithThreeDecimal } from "../../utils";
import { convertValue } from "../AllDeals/DealDetails/conversionUtils";
import { setUpKpiMessage } from "./setUpMessage";
import { calculateCIforGFPfuelTypeLNG, calculateCIforGFPfuelTypeNG } from "./utils";

// Helper function to check if EVP is T&S Products
// Debug import removed
const isTSProductsEVP = (evp: string): boolean => {
  const normalizedEvp = (evp || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return normalizedEvp === "tsproducts";
};

// Helper function to get exclude columns based on EVP
const getExcludeTotalColumns = (evp: string): string[] => {
  const isTSProducts = isTSProductsEVP(evp);

  if (isTSProducts) {
    return [ASSET_SCOPE1_CI, VOLUME_WEIGHTED_AVG_CARBON_INT];
  }
  return [AVG_CARBON_INT, ASSET_SCOPE1_CI];
};

const cardCols: string[] = ["KPIs", "Value", "Unit"];

const cardFirstColData: string[] = [
  EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION,
  NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION,
  MARGIN_TO_CARBON_RATIO_AVG,
];

export const decorateCardTableData = (
  myState: IKpiContentData,
  myDispatch: Idispatch,
  dispatch: Idispatch,
  parameters: Iparameters,
  state: ICalculatorFormState
) => {
  const cardRowData: IKpiCardData[] = [];
  const [cardFirstCol, , cardLastCol] = cardCols;
  // Set the unit for the third row dynamically based on EVP context
  const isTSProducts = isTSProductsEVP(state.evp);
  // For T&S Products (products flow) the Margin to Carbon Ratio should show as $/tCO2e;
  // for other flows show the volume-weighted CI unit (gCO2e/MJ).
  const vwAvgCIUnit = isTSProducts ? MCR_AVG_UOM : "gCO2e/MJ";
  const naeUnit = isTSProducts ? "KTPA" : MTCO2E;
  const cardLastColData: ReactNode[] = [
    <>
      {MTCO2E}{" "}
      <Copy
        onClick={() => {
          copyEquityScope12(myState, myDispatch);
        }}
      />
    </>,
    <>
      {naeUnit}{" "}
      <Copy
        onClick={() => {
          copyNaeScope12(myState, myDispatch);
        }}
      />
    </>,
    <>
      {vwAvgCIUnit}{" "}
      <Copy
        onClick={() => {
          copyMCR(myState, myDispatch);
        }}
      />
    </>,
  ];
  if (myState.stepThreeTableData.length > 0) {
    // setting first and last key-value pair for each row object
    for (let i = 0; i < cardFirstColData.length; i++) {
      cardRowData.push({
        [cardFirstCol]: cardFirstColData[i],
        Value: "0.000",
        [cardLastCol]: cardLastColData[i],
      } as unknown as IKpiCardData);
      // set up the values for each row in the Card
      updateNetAbsEmsScopePeakAnnualEmission(cardRowData, i, myState, myDispatch, state);
      updateMarginToCarbonRatioAvg(cardRowData, i, dispatch, myState, myDispatch, state);
      updateEquityScopeCarbonPeakAnnualEmission(cardRowData, i, myState, myDispatch, parameters);
    }
    myDispatch({ type: "setCardTableData", payload: cardRowData });
  }
};

export const decorateTableData = (
  state: ICalculatorFormState,
  dispatch: Idispatch,
  myState: IKpiContentData,
  myDispatch: Idispatch,
  setKpiMessage: Dispatch<SetStateAction<string[]>>,
  data: { parameters: Iparameters; cols: Array<string | number>; firstColData: string[] }
) => {
  const { parameters, firstColData } = data;
  const stepTwoData: IStepTwoTableData[] = [...state.stepTwoTableData];
  // Restore rowData population logic based on firstColData
  // Use original labels for all rows; only emission rows should have KTPA in their label
  const rowData: IKpiTableData[] = firstColData.map((firstCol) => {
    const row: IKpiTableData = { KPI: firstCol };
    // Optionally initialize year columns to '-' or 0.000 as needed
    for (let year = state.startYear; year <= state.endYear; year++) {
      row[year] = "-";
    }
    row["Total"] = "";
    return row;
  });
  // Keep KPI keys canonical. Unit differences for T&S Products are handled
  // at render time in the table so business logic compares against
  // `NET_ABSOLUTE_EMISSIONS` reliably.
  const operatedScopeYearWiseObj: YearWiseData = { techSource: "" };
  const equityScopeYearWiseObj: YearWiseData = { techSource: "" };
  const scope1CmbCrbnIntYearWiseObj: YearWiseData = { techSource: "" };
  const yearWiseKPIrecord: IyearWiseKPIrecord = {
    netAbsEmissionYearWise: [],
    operatedScopeYearWiseObj,
    equityScopeYearWiseObj,
    timeStampDataYearWise: [],
    carbonIntensityYearWise: [],
    carbonIntensityUomYearWise: [],
    carbonEmissionYearWise: [],
    scope1CmbCrbnIntYearWiseObj,
  };
  const equityScopeData: IRecord = {};
  const netAbsEmissionData: IRecord = {};
  const isGasFiredPower =
    state.technologySource.includes(NATURAL_GAS) || state.technologySource.includes("LNG");

  for (let a = 0; a < rowData.length; a++) {
    const rowDataInfo: IKpiTableData = rowData[a];
    const fieldIndex: number = a;
    const firstCol = state.stepTwoTableFirstCol as string; // Use the actual tech source column name

    const currentUpdateFunctions = createUpdateFunctions(
      {
        fieldIndex,
        rowDataInfo,
        equityScopeData,
        netAbsEmissionData,
        yearWiseKPIrecord,
        parameters,
        stepTwoData,
        firstCol,
      },
      state,
      dispatch
    );

    for (let year = state.startYear; year < state.endYear + 1; year++) {
      let functionsTriggered = 0;
      currentUpdateFunctions.forEach(({ condition, update }) => {
        if (condition) {
          functionsTriggered++;
          update(year);
        }
      });

      if (functionsTriggered === 0 && [NET_ABSOLUTE_EMISSIONS, AVG_CARBON_INT].includes(firstCol)) {
        // No functions triggered for this KPI
      }
    }
    updateTotalColumn(rowData[a], state);
  }
  if (isGasFiredPower && Number(state.operatingModel) !== three) {
    setUpKpiMessage(
      Number(myState.equityScope12PeakEmission.replaceAll(",", "")),
      Number(state.efficiency),
      Number(state.operatingModel),
      setKpiMessage,
      state.assetType,
      dispatch
    );
  }
  myDispatch({ type: "setStepThreeTableData", payload: rowData });
  myDispatch({ type: "setEquityScopeDataYearWise", payload: equityScopeData });
  myDispatch({ type: "setNetAbsEmsDataYearWise", payload: netAbsEmissionData });
  dispatch({
    type: "setOperatedScopeYearWiseObj",
    payload: yearWiseKPIrecord.operatedScopeYearWiseObj,
  });
  dispatch({
    type: "setEquityScopeYearWiseObj",
    payload: yearWiseKPIrecord.equityScopeYearWiseObj,
  });
  dispatch({
    type: "setCarbonIntensityYearWise",
    payload: yearWiseKPIrecord.carbonIntensityYearWise,
  });
  dispatch({
    type: "setCarbonIntensityUomYearWise",
    payload: yearWiseKPIrecord.carbonIntensityUomYearWise,
  });
  dispatch({
    type: "setCarbonEmissionYearWise",
    payload: yearWiseKPIrecord.carbonEmissionYearWise,
  });
  dispatch({ type: "setTimeStampDataYearWise", payload: yearWiseKPIrecord.timeStampDataYearWise });
  dispatch({
    type: "setScope1CmbCrbnIntYearWiseObj",
    payload: yearWiseKPIrecord.scope1CmbCrbnIntYearWiseObj,
  });
};

const saveCiDataForSaveKpiData = (
  data: {
    year: number;
    firstCol: string;
    carbonIntForCurrTechSource: number;
    timeStampDataForCurrTechSource: string;
    carbonIntUomForCurrTechSource: string;
  },
  carbonIntensityYearWise: YearWiseData[],
  carbonIntensityUomYearWise: YearWiseData[],
  timeStampDataYearWise: YearWiseData[]
) => {
  const {
    year,
    firstCol,
    carbonIntForCurrTechSource,
    timeStampDataForCurrTechSource,
    carbonIntUomForCurrTechSource,
  } = data;
  // store carbon intensity for final kpi data saving
  let found = false;
  carbonIntensityYearWise.forEach((obj: YearWiseData) => {
    if (obj.techSource === firstCol) {
      obj[year] = carbonIntForCurrTechSource.toString();
      found = true;
    }
  });
  if (!found) {
    const ciData: YearWiseData = { techSource: firstCol };
    ciData[year] = carbonIntForCurrTechSource.toString();
    carbonIntensityYearWise.push(ciData);
  }

  // set UOM for all CI data
  found = false;
  carbonIntensityUomYearWise.forEach((obj: YearWiseData) => {
    if (obj.techSource === firstCol) {
      obj[year] = carbonIntUomForCurrTechSource;
      found = true;
    }
  });
  if (!found) {
    const ciData: YearWiseData = { techSource: firstCol };
    ciData[year] = carbonIntUomForCurrTechSource;
    carbonIntensityUomYearWise.push(ciData);
  }

  // set UOM for all CI data
  found = false;
  timeStampDataYearWise.forEach((obj: YearWiseData) => {
    if (obj.techSource === firstCol) {
      obj[year] = timeStampDataForCurrTechSource;
      found = true;
    }
  });
  if (!found) {
    const ciData: YearWiseData = { techSource: firstCol };
    ciData[year] = timeStampDataForCurrTechSource;
    timeStampDataYearWise.push(ciData);
  }

  return [carbonIntensityYearWise, carbonIntensityUomYearWise, timeStampDataYearWise];
};

const getUOMForPipelineGasAsCommodity = (parameters: Iparameters) => {
  // UOM Conversion Factor is (3.6*0.293/1000000000) in MTPA for Americas
  // UOM Conversion Factor is (3.6/1000000000)  in MTPA for Rest of the world
  // UOM Conversion Factor is (10^9 / 10^12 = 0.001)  in MTPA for Australia
  const uomForAmericasAndAsiaWithCommodityPg =
    (ThreePointSix * PointTwoNineThree) / (thousand * thousand * thousand); // in MTPA
  const uomForRestOfTheWorldWithCommodityPg = ThreePointSix / (thousand * thousand * thousand); // in MTPA
  const uomForAustraliaWithCommodityPg = 0.001;
  if (parameters.isAmericasWithCommodityPg || parameters.isAsiaWithCommodityPg) {
    return uomForAmericasAndAsiaWithCommodityPg;
  }
  if (parameters.isAustraliaWithCommodityPg) {
    return uomForAustraliaWithCommodityPg;
  }
  return uomForRestOfTheWorldWithCommodityPg;
};

const getUOMForRngAsCommodity = (parameters: Iparameters) => {
  // UOM Conversion Factor is (3.6*0.293/1000000000) in MTPA for Americas
  // UOM Conversion Factor is (3.6/1000000000)  in MTPA for Rest of the world
  // UOM Conversion Factor is (10^9 / 10^12 = 0.001)  in MTPA for Australia
  const uomForAmericasAndAsiaWithCommodityRng =
    (ThreePointSix * PointTwoNineThree) / (thousand * thousand * thousand); // in MTPA
  const uomForRestOfTheWorldWithCommodityRng = ThreePointSix / (thousand * thousand * thousand); // in MTPA
  const uomForAustraliaWithCommodityRng = 0.001;
  if (parameters.isAmericasWithCommodityRng || parameters.isAsiaWithCommodityRng) {
    return uomForAmericasAndAsiaWithCommodityRng;
  }
  if (parameters.isAustraliaWithCommodityRng) {
    return uomForAustraliaWithCommodityRng;
  }
  return uomForRestOfTheWorldWithCommodityRng;
};

const getCIforTechSrcNonGridAndNonGFP = (
  year: number,
  firstCol: string,
  carbonIntensityYearWise: YearWiseData[],
  carbonIntensityUomYearWise: YearWiseData[],
  timeStampDataYearWise: YearWiseData[],
  state: ICalculatorFormState
) => {
  let carbonIntForCurrTechSource = 0;
  let carbonIntUomForCurrTechSource = "";
  let timeStampDataForCurrTechSource = "";
  for (const carbonIntensityForNonGrid of state.carbonIntensityForNonGrid) {
    if (carbonIntensityForNonGrid.TECHNOLOGY_SOURCE_NAME === firstCol) {
      carbonIntForCurrTechSource = carbonIntensityForNonGrid.CARBON_INTENSITY;
      carbonIntUomForCurrTechSource = carbonIntensityForNonGrid.UNIT_CODE;
      timeStampDataForCurrTechSource = carbonIntensityForNonGrid.LAST_UPDATE_TIMESTAMP;
      // store carbon intensity for final kpi data saving
      [carbonIntensityYearWise, carbonIntensityUomYearWise, timeStampDataYearWise] =
        saveCiDataForSaveKpiData(
          {
            year,
            firstCol,
            carbonIntForCurrTechSource,
            timeStampDataForCurrTechSource,
            carbonIntUomForCurrTechSource,
          },
          carbonIntensityYearWise,
          carbonIntensityUomYearWise,
          timeStampDataYearWise
        );
    }
  }
  return carbonIntForCurrTechSource;
};

const getCIforTechSrcGrid = (
  year: number,
  firstCol: string,
  carbonIntensityYearWise: YearWiseData[],
  carbonIntensityUomYearWise: YearWiseData[],
  timeStampDataYearWise: YearWiseData[],
  state: ICalculatorFormState
) => {
  let carbonIntForCurrTechSource = 0;
  let carbonIntUomForCurrTechSource = "";
  let timeStampDataForCurrTechSource = "";
  for (const carbonIntensityForGrid of state.carbonIntensityForGrid) {
    if (carbonIntensityForGrid.YEAR === year) {
      carbonIntForCurrTechSource = carbonIntensityForGrid.CARBON_INTENSITY;
      carbonIntUomForCurrTechSource = carbonIntensityForGrid.UNIT_CODE;
      timeStampDataForCurrTechSource = carbonIntensityForGrid.LAST_UPDATE_TIMESTAMP;
      // store carbon intensity for final kpi data saving
      [carbonIntensityYearWise, carbonIntensityUomYearWise, timeStampDataYearWise] =
        saveCiDataForSaveKpiData(
          {
            year,
            firstCol,
            carbonIntForCurrTechSource,
            timeStampDataForCurrTechSource,
            carbonIntUomForCurrTechSource,
          },
          carbonIntensityYearWise,
          carbonIntensityUomYearWise,
          timeStampDataYearWise
        );
    }
  }
  return carbonIntForCurrTechSource;
};

const getCIforCommodityPG = (
  year: number,
  firstCol: string,
  carbonIntensityYearWise: YearWiseData[],
  carbonIntensityUomYearWise: YearWiseData[],
  timeStampDataYearWise: YearWiseData[],
  state: ICalculatorFormState
) => {
  let carbonIntForCurrTechSource = 0;
  let carbonIntUomForCurrTechSource = "";
  let timeStampDataForCurrTechSource = "";
  for (const carbonIntensityForGas of state.carbonIntensityForGas) {
    if (carbonIntensityForGas.YEAR === year) {
      carbonIntForCurrTechSource = carbonIntensityForGas.CARBON_INTENSITY;
      carbonIntUomForCurrTechSource = carbonIntensityForGas.CARBON_INTENSITY_UNIT;
      timeStampDataForCurrTechSource = carbonIntensityForGas.LAST_UPDATE_TIMESTAMP;
      // store carbon intensity for final kpi data saving
      [carbonIntensityYearWise, carbonIntensityUomYearWise, timeStampDataYearWise] =
        saveCiDataForSaveKpiData(
          {
            year,
            firstCol,
            carbonIntForCurrTechSource,
            timeStampDataForCurrTechSource,
            carbonIntUomForCurrTechSource,
          },
          carbonIntensityYearWise,
          carbonIntensityUomYearWise,
          timeStampDataYearWise
        );
    }
  }
  return carbonIntForCurrTechSource;
};

const getCIforCommodityRNG = (
  year: number,
  firstCol: string,
  carbonIntensityYearWise: YearWiseData[],
  carbonIntensityUomYearWise: YearWiseData[],
  timeStampDataYearWise: YearWiseData[],
  state: ICalculatorFormState
) => {
  let carbonIntForCurrTechSource = 0;
  let carbonIntUomForCurrTechSource = "";
  let timeStampDataForCurrTechSource = "";
  for (const carbonIntensityForRng of state.carbonIntensityForRng) {
    if (carbonIntensityForRng.YEAR === year) {
      carbonIntForCurrTechSource = carbonIntensityForRng.CARBON_INTENSITY;
      carbonIntUomForCurrTechSource = carbonIntensityForRng.CARBON_INTENSITY_UNIT;
      timeStampDataForCurrTechSource = carbonIntensityForRng.LAST_UPDATE_TIMESTAMP;
      // store carbon intensity for final kpi data saving
      [carbonIntensityYearWise, carbonIntensityUomYearWise, timeStampDataYearWise] =
        saveCiDataForSaveKpiData(
          {
            year,
            firstCol,
            carbonIntForCurrTechSource,
            timeStampDataForCurrTechSource,
            carbonIntUomForCurrTechSource,
          },
          carbonIntensityYearWise,
          carbonIntensityUomYearWise,
          timeStampDataYearWise
        );
    }
  }
  return carbonIntForCurrTechSource;
};

const saveCarbonEmissionDataForSaveKpiData = (
  firstCol: string,
  year: number,
  carbonEmissionYearWise: YearWiseData[],
  currentCarbonEmission: number
) => {
  let found = false;
  carbonEmissionYearWise.forEach((obj: YearWiseData) => {
    if (obj.techSource === firstCol) {
      obj[year] = currentCarbonEmission.toString();
      found = true;
    }
  });
  if (!found) {
    const carbonEmissionData: YearWiseData = { techSource: firstCol };
    carbonEmissionData[year] = currentCarbonEmission.toString();
    carbonEmissionYearWise.push(carbonEmissionData);
  }
  return carbonEmissionYearWise;
};

// Helper: Calculate Net Absolute Emission for T&S Products
// Excel-aligned: Return NAE in KTPA for table, only divide by 1000 for summary MTCO2e
const calculateNetAbsEmissionForTSProducts = (
  year: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
): number => {
  // Excel logic: NAE = SUM(NAE for each product) - Carbon Credits (all in KTPA)
  let totalNAE = 0;
  const extractedProducts: Array<{
    product: string;
    volume: number;
    conversionFactor: number;
    intensity: number;
  }> = [];
  for (let index = 0; index < stepTwoData.length; index++) {
    // Use state.stepTwoTableFirstCol for product extraction (aligns with VWAvgCI and Cat11)
    const productKey = state.stepTwoTableFirstCol;
    const commodity = stepTwoData[index][
      productKey as keyof (typeof stepTwoData)[typeof index]
    ] as string;
    // Skip rows with undefined product name, 'Total Volume', or 'Carbon Credit'
    if (
      !commodity ||
      commodity.trim() === "" ||
      commodity === TABLE_ROW_TOTAL_VOLUME ||
      commodity.toLowerCase().includes("carbon credit")
    )
      continue;
    // NAE for each product = (Energy in MJ × Intensity) / 1e9 (to get KTPA)
    const volume = parseVolumeFromTableData(stepTwoData[index][year]);
    const conversionFactor = MJ_PER_KBBL[commodity] || 0;
    const energyInMJ = volume * conversionFactor;
    const carbonIntensity = getCarbonIntensityForTSProducts(
      commodity,
      state.productCarbonIntensities
    );

    extractedProducts.push({
      product: commodity,
      volume,
      conversionFactor,
      intensity: carbonIntensity,
    });
    const naeKTPA = (energyInMJ * carbonIntensity) / 1e9;
    totalNAE += naeKTPA;
  }

  let carbonReduction = 0;
  if (state.carbonCredit === 1) {
    const carbonCreditsRow = stepTwoData.find((row) =>
      String(row[state.stepTwoTableFirstCol as keyof typeof row])
        .toLowerCase()
        .includes("carbon credit")
    );
    const carbonCreditsValue = carbonCreditsRow ? carbonCreditsRow[year] : 0;
    carbonReduction =
      typeof carbonCreditsValue === "number"
        ? carbonCreditsValue
        : Number((carbonCreditsValue || "").toString().replace(/,/g, ""));
  }
  const netEmissionKTPA = totalNAE - carbonReduction;
  return netEmissionKTPA;
};

// Helper: Calculate Net Absolute Emission for other commodities (Power, Pipeline Gas, RNG)
const calculateNetAbsEmissionForOtherCommodities = (
  year: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[],
  yearWiseKPIrecord: IyearWiseKPIrecord,
  parameters: Iparameters,
  firstCol: string,
  dispatch: Idispatch
): number => {
  let netAbsEmission = 0;

  for (let index = 0; index < stepTwoData.length - 1; index++) {
    const firstColInfo: string = stepTwoData[index][
      firstCol as keyof (typeof stepTwoData)[typeof index]
    ] as string;

    let carbonIntForCurrTechSource = 0;

    if (parameters.isCommodityPW && firstColInfo !== "Grid") {
      carbonIntForCurrTechSource = getCIforTechSrcNonGridAndNonGFP(
        year,
        firstColInfo,
        yearWiseKPIrecord.carbonIntensityYearWise,
        yearWiseKPIrecord.carbonIntensityUomYearWise,
        yearWiseKPIrecord.timeStampDataYearWise,
        state
      );
    } else if (parameters.isCommodityPW && firstColInfo === "Grid") {
      carbonIntForCurrTechSource = getCIforTechSrcGrid(
        year,
        firstColInfo,
        yearWiseKPIrecord.carbonIntensityYearWise,
        yearWiseKPIrecord.carbonIntensityUomYearWise,
        yearWiseKPIrecord.timeStampDataYearWise,
        state
      );
    } else if (parameters.isCommodityPg) {
      carbonIntForCurrTechSource = getCIforCommodityPG(
        year,
        firstColInfo,
        yearWiseKPIrecord.carbonIntensityYearWise,
        yearWiseKPIrecord.carbonIntensityUomYearWise,
        yearWiseKPIrecord.timeStampDataYearWise,
        state
      );
    } else if (parameters.isCommodityRng) {
      carbonIntForCurrTechSource = getCIforCommodityRNG(
        year,
        firstColInfo,
        yearWiseKPIrecord.carbonIntensityYearWise,
        yearWiseKPIrecord.carbonIntensityUomYearWise,
        yearWiseKPIrecord.timeStampDataYearWise,
        state
      );
    } else if (state.evp === "SLMT") {
      // For SLMT, use the carbon intensity from API data
      carbonIntForCurrTechSource = getCarbonIntensityForSLMT(year, state);
    } else {
      // For other commodities, use appropriate carbon intensity getter
    }

    netAbsEmission = calculateNAE(
      { carbonIntForCurrTechSource, index, year, firstColInfo, netAbsEmission, parameters },
      yearWiseKPIrecord.carbonEmissionYearWise,
      stepTwoData,
      state,
      dispatch
    );
  }

  return netAbsEmission;
};

const calculateNAE = (
  data: {
    carbonIntForCurrTechSource: number;
    index: number;
    year: number;
    firstColInfo: string;
    netAbsEmission: number;
    parameters: Iparameters;
  },
  carbonEmissionYearWise: YearWiseData[],
  stepTwoData: IStepTwoTableData[],
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  const { carbonIntForCurrTechSource, index, year, firstColInfo, parameters } = data;

  const gasFiredPowerParams = calculateCIforGasFiredPower(state, dispatch, stepTwoData);
  // calculate NAE for power but not gas fired power
  if (parameters.isCommodityPW && !["LNG", NATURAL_GAS].includes(firstColInfo)) {
    let tableCellValue: number | string = stepTwoData[index][year];
    if (typeof tableCellValue !== "number") {
      tableCellValue = Number(stepTwoData[index][year].toString().replaceAll(",", ""));
    }

    data.netAbsEmission +=
      (tableCellValue * carbonIntForCurrTechSource) / (thousand * thousand * thousand);
    // save carbon emission data separately to use for save kpi data
    const currentCarbonEmission =
      (tableCellValue * carbonIntForCurrTechSource) / (thousand * thousand * thousand);
    saveCarbonEmissionDataForSaveKpiData(
      firstColInfo,
      year,
      carbonEmissionYearWise,
      currentCarbonEmission
    );
  } else if (parameters.isCommodityPg) {
    // calculate nae for gas
    // 'Volume * 0.9 * Carbon Intensity * UOM Conversion Factor where 0.9 is for HHV to LHV conversion factor
    // UOM Conversion Factor is (3.6*0.293/1000000000) in MTPA for Americas
    // UOM Conversion Factor is (3.6/1000000000)  in MTPA for Rest of the world
    // UOM Conversion Factor is (10^9 / 10^12 = 0.001)  in MTPA for Australia
    const uomForPipelineGas: number = getUOMForPipelineGasAsCommodity(parameters);
    let tableCellValue = stepTwoData[index][year];
    // convert tableCellValue to number
    if (typeof tableCellValue !== "number") {
      tableCellValue = Number(stepTwoData[index][year].toString().replaceAll(",", ""));
    }
    // All Asia with natural gas (MMBTU) - same as isAmericasWithCommodityPg, CI is always gCO2e/MJ
    // All Australia with Natural gas where UOM is PJ -
    // Net Absolute Emissions = Volume HHV in PJ *  0.9 (HHV to LHV) * Carbon Intensity for Rest of the World LHV in gCO2e/MJ * {10^9 (MJ/PJ) / 10^12 (gCO2e/MTPA)}
    // created a commonCalc that is the common calculation for all the cases.
    const commonCalc: number = tableCellValue * pointnine * carbonIntForCurrTechSource;
    const emissionForThisYear = commonCalc * uomForPipelineGas;
    data.netAbsEmission += emissionForThisYear; //  in MTPA
    const currentCarbonEmission = emissionForThisYear;
    saveCarbonEmissionDataForSaveKpiData(
      firstColInfo,
      year,
      carbonEmissionYearWise,
      currentCarbonEmission
    );
  } else if (parameters.isGasFiredPower) {
    // finally if Gas Fired power is there, add nae of gas fired power to latest nae calculated
    data.netAbsEmission += gasFiredPowerParams.nae[year];
    saveCarbonEmissionDataForSaveKpiData(
      firstColInfo,
      year,
      carbonEmissionYearWise,
      gasFiredPowerParams.nae[year]
    );
  } else if (parameters.isCommodityRng) {
    // calculate nae for RNG
    // 'Volume * 0.9 * Carbon Intensity * UOM Conversion Factor where 0.9 is for HHV to LHV conversion factor
    // UOM Conversion Factor is (3.6*0.293/1000000000) in MTPA for Americas
    // UOM Conversion Factor is (3.6/1000000000)  in MTPA for Rest of the world
    // UOM Conversion Factor is (10^9 / 10^12 = 0.001)  in MTPA for Australia
    const uomForRng: number = getUOMForRngAsCommodity(parameters);
    let tableCellValue = stepTwoData[index][year];
    // convert tableCellValue to number
    if (typeof tableCellValue !== "number") {
      tableCellValue = Number(stepTwoData[index][year].toString().replaceAll(",", ""));
    }
    // All Asia with natural gas (MMBTU) - same as isAmericasWithCommodityPg, CI is always gCO2e/MJ
    // All Australia with Natural gas where UOM is PJ -
    // Net Absolute Emissions = Volume HHV in PJ *  0.9 (HHV to LHV) * Carbon Intensity for Rest of the World LHV in gCO2e/MJ * {10^9 (MJ/PJ) / 10^12 (gCO2e/MTPA)}
    // created a commonCalc that is the common calculation for all the cases.
    const commonCalc: number = tableCellValue * pointnine * carbonIntForCurrTechSource;
    const emissionForThisYear = commonCalc * uomForRng;
    data.netAbsEmission += emissionForThisYear; //  in MTPA
    const currentCarbonEmission = emissionForThisYear;
    saveCarbonEmissionDataForSaveKpiData(
      firstColInfo,
      year,
      carbonEmissionYearWise,
      currentCarbonEmission
    );
  } else if (state.evp === "SLMT") {
    // Calculate NAE for SLMT
    // TODO: Conversion factors should be retrieved from API once backend is ready
    // API should return: TBTU_HHV_TO_MT_LNG and LNG_KG_TO_MJ_LHV for the selected commodity
    // Formula: (Volume TBTU * 50.1 * CI) / (52.69 * 1000)
    // Where: 52.69 = TBTU HHV to MT LNG, 50.1 = MT LNG to MJ LHV
    let tableCellValue = stepTwoData[index][year];
    // convert tableCellValue to number
    if (typeof tableCellValue !== "number") {
      tableCellValue = Number(stepTwoData[index][year].toString().replaceAll(",", ""));
    }

    // Deal NAE = (Volume TBTU * 50.1 * CI gCO2e/MJ) / (52.69 * 1000) = mtCO2e
    const emissionForThisYear =
      (tableCellValue * SLMT_CONVERSION_FACTORS.LNG_KG_TO_MJ_LHV * carbonIntForCurrTechSource) /
      (SLMT_CONVERSION_FACTORS.TBTU_HHV_TO_MT_LNG * thousand);

    data.netAbsEmission += emissionForThisYear; // in MTPA
    const currentCarbonEmission = emissionForThisYear;
    saveCarbonEmissionDataForSaveKpiData(
      firstColInfo,
      year,
      carbonEmissionYearWise,
      currentCarbonEmission
    );
  }
  return data.netAbsEmission;
};

const updateNetAbsEmission = (
  data: {
    yearWiseKPIrecord: IyearWiseKPIrecord;
    year: number;
    rowDataInfo: IKpiTableData;
    netAbsEmissionData: Record<number, string | number>;
    parameters: Iparameters;
    firstCol: string;
  },
  stepTwoData: IStepTwoTableData[],
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  const { yearWiseKPIrecord, year, rowDataInfo, netAbsEmissionData, parameters, firstCol } = data;
  const isTSProducts = isTSProductsEVP(state.evp); // Use EVP check instead

  const netAbsEmission = isTSProducts
    ? calculateNetAbsEmissionForTSProducts(year, state, stepTwoData)
    : calculateNetAbsEmissionForOtherCommodities(
        year,
        state,
        stepTwoData,
        yearWiseKPIrecord,
        parameters,
        firstCol,
        dispatch
      );

  if (isNaN(netAbsEmission)) {
    rowDataInfo[year] = "-";
  } else {
    // Net Absolute Emissions (MTPA) calculated
    netAbsEmissionData[year] = netAbsEmission;
    rowDataInfo[year] = convertInputToCommaSeparatedWithThreeDecimal(netAbsEmission.toFixed(three));
  }
  yearWiseKPIrecord.netAbsEmissionYearWise[year] = netAbsEmission;
};

const updateScope1CmbCrbnInt = (
  year: number,
  rowData: IKpiTableData,
  scope1CmbCrbnIntYearWiseObj: YearWiseData,
  state: ICalculatorFormState,
  dispatch: Idispatch,
  stepTwoData: IStepTwoTableData[]
) => {
  const gasFiredPowerParams = calculateCIforGasFiredPower(state, dispatch, stepTwoData);
  // convert upto 3 decimals while showing in UI
  rowData[year] = convertInputToCommaSeparatedWithThreeDecimal(
    gasFiredPowerParams.assetScope1[year].toFixed(three)
  );
  scope1CmbCrbnIntYearWiseObj[year] = rowData[year];
  scope1CmbCrbnIntYearWiseObj.techSource = state.fuelType;
  dispatch({ type: "setScope1CmbCrbnInt", payload: { year, value: rowData[year] } });
};

const updateEquityScope = (
  year: number,
  rowData: IKpiTableData,
  equityScopeYearWiseObj: YearWiseData,
  equityScopeData: IRecord,
  state: ICalculatorFormState,
  dispatch: Idispatch,
  stepTwoData: IStepTwoTableData[]
) => {
  const gasFiredPowerParams = calculateCIforGasFiredPower(state, dispatch, stepTwoData);
  // convert upto 3 decimals while showing in UI
  rowData[year] = convertInputToCommaSeparatedWithThreeDecimal(
    gasFiredPowerParams.equityScope12[year].toFixed(three)
  );
  equityScopeYearWiseObj[year] = rowData[year];
  equityScopeData[year] = gasFiredPowerParams.equityScope12[year];
  equityScopeYearWiseObj.techSource = state.fuelType;
  dispatch({ type: "setEquityScope", payload: rowData[year] });
};

const updateOperatedScope = (
  year: number,
  rowData: IKpiTableData,
  operatedScopeYearWiseObj: YearWiseData,
  state: ICalculatorFormState,
  dispatch: Idispatch,
  stepTwoData: IStepTwoTableData[]
) => {
  const gasFiredPowerParams = calculateCIforGasFiredPower(state, dispatch, stepTwoData);
  // convert upto 3 decimals while showing in UI
  rowData[year] = convertInputToCommaSeparatedWithThreeDecimal(
    gasFiredPowerParams.operatedScope12[year].toFixed(three)
  );
  operatedScopeYearWiseObj[year] = rowData[year];
  operatedScopeYearWiseObj.techSource = state.fuelType;
  dispatch({ type: "setOperatedScope", payload: rowData[year] });
};

const updateAvgCarbonIntensity = (
  netAbsEmissionYearWise: Record<number, string | number>,
  year: number,
  rowData: IKpiTableData,
  parameters: Iparameters,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
) => {
  let avgCarbonInt = 0;

  // Note: T&S Products EVP no longer uses this function (they use Volume-weighted CI instead)
  // This function is for: Power, Pipeline Gas, and RNG commodities

  if (parameters.isCommodityPW && stepTwoData.length > 0) {
    // UOM conversion factor = 1/1000 = 0.0001
    //
    // calculation for avg carbon intensity for power =>
    // ((vol of techsrc 1 for 2023 * CI of techsrc 1 for 2023 + vol of techsrc 2 for 2023 * CI of techsrc 2 for 2023....+ Vol of LNG/NG * PVCE if available) * 1000) /
    // (vol of techsrc 1 for 2023+ vol of techsrc 2 for 2023 + vol of LNG/NG if available)
    // OR
    // (NAE in MTPA / total volume in MWh) * 10^6 tCO2e/MWh
    const netAbsEmissionForCurrYear = netAbsEmissionYearWise[year];
    let totalVolumeFinal = stepTwoData[stepTwoData.length - 1][year];
    if (typeof totalVolumeFinal !== "number") {
      totalVolumeFinal = Number(totalVolumeFinal.toString().replaceAll(",", ""));
    }
    // final formula
    avgCarbonInt = (Number(netAbsEmissionForCurrYear) / totalVolumeFinal) * tenlakh;
  } else if (
    parameters.isCommodityPg &&
    stepTwoData.length > 0 &&
    state.carbonIntensityForGas.length > 0
  ) {
    const totalCarbonInt = state.carbonIntensityForGas.reduce(
      (total: number, item: { CARBON_INTENSITY: number }) => total + item.CARBON_INTENSITY,
      0
    );
    // a simple averaging of all the carbon intensity values for Natural gas
    avgCarbonInt = totalCarbonInt / state.carbonIntensityForGas.length;
  } else if (
    parameters.isCommodityRng &&
    stepTwoData.length > 0 &&
    state.carbonIntensityForRng.length > 0
  ) {
    const totalCarbonInt = state.carbonIntensityForRng.reduce(
      (total: number, item: { CARBON_INTENSITY: number }) => total + item.CARBON_INTENSITY,
      0
    );
    // a simple averaging of all the carbon intensity values for RNG
    avgCarbonInt = totalCarbonInt / state.carbonIntensityForRng.length;
  } else if (state.evp === "SLMT" && stepTwoData.length > 0) {
    // For SLMT, use static carbon intensity data
    avgCarbonInt = getCarbonIntensityForSLMT(year, state);

    // Deal Volume (MJ) = (Volume TBTU / 52.69) * 50.1 * 10^9
  } else {
    console.warn(
      "⚠️ Cannot calculate average carbon intensity: Missing or invalid data for year",
      year
    );
  }

  if (isNaN(avgCarbonInt)) {
    rowData[year] = "-";
  } else {
    // Avg Carbon Intensity calculated
    rowData[year] = avgCarbonInt.toFixed(three);
  }
};

// Helper: Parse volume value from step two table data
const parseVolumeFromTableData = (volumeData: string | number): number => {
  if (typeof volumeData !== "number") {
    return Number(volumeData.toString().replaceAll(",", ""));
  }
  return volumeData;
};

// Helper: Get carbon intensity for non-grid technology sources
const getCarbonIntensityForNonGrid = (techSource: string, state: ICalculatorFormState): number => {
  for (const ci of state.carbonIntensityForNonGrid) {
    if (ci.TECHNOLOGY_SOURCE_NAME === techSource) {
      return ci.CARBON_INTENSITY;
    }
  }
  return 0;
};

// Helper: Get carbon intensity for grid by year
const getCarbonIntensityForGrid = (year: number, state: ICalculatorFormState): number => {
  for (const ci of state.carbonIntensityForGrid) {
    if (ci.YEAR === year) {
      return ci.CARBON_INTENSITY;
    }
  }
  return 0;
};

// Helper: Get carbon intensity for pipeline gas by year
const getCarbonIntensityForPipelineGas = (year: number, state: ICalculatorFormState): number => {
  for (const ci of state.carbonIntensityForGas) {
    if (ci.YEAR === year) {
      return ci.CARBON_INTENSITY;
    }
  }
  return 0;
};

// Helper: Get carbon intensity for RNG by year
const getCarbonIntensityForRNG = (year: number, state: ICalculatorFormState): number => {
  for (const ci of state.carbonIntensityForRng) {
    if (ci.YEAR === year) {
      return ci.CARBON_INTENSITY;
    }
  }
  return 0;
};

const getCarbonIntensityForSLMT = (year: number, state: ICalculatorFormState) => {
  const ciArray = state.carbonIntensityForSLMT;
  const found = ciArray.find((item: ICarbonIntensityForSLMT) => item.YEAR === year);
  return found ? found.CARBON_INTENSITY : 0;
};

// Helper: Get carbon intensity for T&S Products commodity (from API data stored in state)
// Uses region-specific intensity values from the backend API
// Falls back to 0 if product/region not found in API data
const getCarbonIntensityForTSProducts = (
  commodityName: string,
  productIntensities: IProductCarbonIntensity[]
): number => {
  // Find matching product and region in the API data (case-insensitive comparison for region)
  const productData = productIntensities.find((item) => item.PRODUCT_NAME === commodityName);

  if (productData) {
    return productData.CARBON_INTENSITY;
  }

  // If no exact match, try to find Default region for this product
  const defaultData = productIntensities.find((item) => item.PRODUCT_NAME === commodityName);

  if (defaultData) {
    return defaultData.CARBON_INTENSITY;
  }

  console.warn("⚠️ No carbon intensity found for:", {
    commodityName,
    availableProducts: productIntensities.map((p) => ({
      product: p.PRODUCT_NAME,
      region: p.REGION_NAME,
    })),
  });
  return 0;
};

// Helper: Get CAT11 factor for T&S Products commodity (from static CAT11 factors)
// Uses Scope 3 CAT11 (Tank to Wheel) CI Factors from Excel specification
const getCAT11FactorForTSProducts = (commodityName: string): number => {
  const cat11Data = TS_PRODUCTS_CAT11_FACTORS[commodityName];
  return cat11Data ? cat11Data.value : 0;
};

// Helper: Calculate volume-weighted average for Power commodity
const calculateVolumeWeightedAvgForPower = (
  year: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
): number => {
  let totalEnergyTimesIntensity = 0;
  let totalEnergy = 0;

  for (let index = 0; index < stepTwoData.length - 1; index++) {
    const techSource = stepTwoData[index][
      state.stepTwoTableFirstCol as keyof (typeof stepTwoData)[typeof index]
    ] as string;

    // Skip Grid and Gas Fired sources
    if (["Grid", "LNG", NATURAL_GAS].includes(techSource)) {
      continue;
    }

    const volume = parseVolumeFromTableData(stepTwoData[index][year]);
    const carbonIntensity = getCarbonIntensityForNonGrid(techSource, state);

    totalEnergyTimesIntensity += volume * carbonIntensity;
    totalEnergy += volume;
  }

  return totalEnergy > 0 ? totalEnergyTimesIntensity / totalEnergy : 0;
};

// Helper: Calculate simple average carbon intensity
const calculateSimpleAvgCarbonIntensity = (
  carbonIntensityData: Array<{ CARBON_INTENSITY: number }>
): number => {
  const totalCarbonInt = carbonIntensityData.reduce(
    (total: number, item: { CARBON_INTENSITY: number }) => total + item.CARBON_INTENSITY,
    0
  );
  return carbonIntensityData.length > 0 ? totalCarbonInt / carbonIntensityData.length : 0;
};

// New: Calculate Volume-weighted Average Carbon Intensity
// Formula: Σ(Energy Delivered × Intensity) / Σ(Energy Delivered)
// This provides a weighted average based on actual energy volumes per technology source
// Note: This KPI is ONLY displayed for T&S Products EVP
// Calculate Volume-weighted Average Carbon Intensity
// For T&S Products: Calculated in getNcidataForStepThreeTable with carbon credits effect
// For other EVPs: Uses standard weighted average calculations
const updateVolumeWeightedAvgCI = (
  year: number,
  rowData: IKpiTableData,
  parameters: Iparameters,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
) => {
  let volumeWeightedAvgCI = 0;

  if (stepTwoData.length === 0) {
    rowData[year] = "-";
    return;
  }

  // Check if this is a T&S Products EVP
  const isTSProducts = isTSProductsEVP(state.evp);

  if (isTSProducts) {
    // For T&S Products: Skip calculation here as it's handled in getNcidataForStepThreeTable
    // where carbon credits effect and proper volume weighting is applied
    return;
  } else if (parameters.isCommodityPW) {
    // For Power commodity
    volumeWeightedAvgCI = calculateVolumeWeightedAvgForPower(year, state, stepTwoData);
  } else if (parameters.isCommodityPg) {
    // For Pipeline Gas commodity
    volumeWeightedAvgCI = calculateSimpleAvgCarbonIntensity(state.carbonIntensityForGas);
  } else if (parameters.isCommodityRng) {
    // For RNG commodity
    volumeWeightedAvgCI = calculateSimpleAvgCarbonIntensity(state.carbonIntensityForRng);
  }

  rowData[year] = isNaN(volumeWeightedAvgCI) ? "-" : volumeWeightedAvgCI.toFixed(3);
};

// Helper: Calculate Cat11 emission for Power commodity (non-Grid)
const calculateCat11ForPowerNonGrid = (
  year: number,
  index: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[],
  techSource: string
): number => {
  const carbonIntensity = getCarbonIntensityForNonGrid(techSource, state);
  const volume = parseVolumeFromTableData(stepTwoData[index][year]);
  return (volume * carbonIntensity) / (thousand * thousand * thousand);
};

// Helper: Calculate Cat11 emission for Grid
const calculateCat11ForGrid = (
  year: number,
  index: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
): number => {
  const carbonIntensity = getCarbonIntensityForGrid(year, state);
  const volume = parseVolumeFromTableData(stepTwoData[index][year]);
  return (volume * carbonIntensity) / (thousand * thousand * thousand);
};

// Helper: Calculate Cat11 emission for Pipeline Gas
const calculateCat11ForPipelineGas = (
  year: number,
  index: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[],
  parameters: Iparameters
): number => {
  const carbonIntensity = getCarbonIntensityForPipelineGas(year, state);
  const volume = parseVolumeFromTableData(stepTwoData[index][year]);
  const uomForPipelineGas = getUOMForPipelineGasAsCommodity(parameters);
  const commonCalc = volume * pointnine * carbonIntensity;
  return commonCalc * uomForPipelineGas;
};

// Helper: Calculate Cat11 emission for RNG
const calculateCat11ForRNG = (
  year: number,
  index: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[],
  parameters: Iparameters
): number => {
  const carbonIntensity = getCarbonIntensityForRNG(year, state);
  const volume = parseVolumeFromTableData(stepTwoData[index][year]);
  const uomForRng = getUOMForRngAsCommodity(parameters);
  const commonCalc = volume * pointnine * carbonIntensity;
  return commonCalc * uomForRng;
};

// Helper: Calculate Cat11 emission for T&S Products commodities
// Uses static CAT11 (Tank to Wheel) factors from Excel specification
// Formula: Volume (KBBL) × Conversion Factor (KBBL to MJ) × CAT11 Factor (gCO2e/MJ) / 1,000,000 (to get KTPA)
// Standard conversion: 1 tonne = 1,000,000 grams. Cat 11 emission is calculated in grams, so divide by 1,000,000 to get tonnes (KTPA).
const calculateCat11ForTSProducts = (
  year: number,
  index: number,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[],
  commodity: string
): number => {
  const volume = parseVolumeFromTableData(stepTwoData[index][year]);
  const cat11Factor = getCAT11FactorForTSProducts(commodity);

  // Volume is in KBBL, need to convert to MJ using conversion factors from Excel
  // Conversion factors (KBBL to MJ_LHV) are different for each product type
  // From Excel: Gasoline=5262479.08, Diesel=5834833.91, Kerosene=5675846.61, etc.
  const conversionFactor = MJ_PER_KBBL[commodity] || 0;

  // Energy in MJ = Volume (KBBL) × Conversion Factor
  const energyInMJ = volume * conversionFactor;

  // Cat11 Emission (gCO2) = Energy (MJ) × CAT11 Factor (gCO2e/MJ)
  // Convert to KTPA: gCO2 / 1,000,000,000 (g to KTPA)
  // 1 KTPA = 1,000 tonnes = 1,000,000 kg = 1,000,000,000 grams
  const result = (energyInMJ * cat11Factor) / 1_000_000_000;

  return result;
};

// New: Calculate Scope 3 Category 11 Emission
// Formula: Energy Delivered (MJ) × Cat11 Factors
// Convert to KTPA: (gCO2 × 10^-9)
// Note: This KPI is ONLY displayed for T&S Products EVP
// Uses CAT11 (Tank to Wheel) factors from Excel specification for T&S Products
const updateScope3Cat11Emission = (
  year: number,
  rowData: IKpiTableData,
  parameters: Iparameters,
  state: ICalculatorFormState,
  stepTwoData: IStepTwoTableData[]
) => {
  let scope3Cat11Emission = 0;

  // Check if this is a T&S Products EVP
  const isTSProducts = isTSProductsEVP(state.evp);

  for (let index = 0; index < stepTwoData.length - 1; index++) {
    const techSourceOrCommodity = stepTwoData[index][
      state.stepTwoTableFirstCol as keyof (typeof stepTwoData)[typeof index]
    ] as string;

    // ==================================================================================
    // T&S PRODUCTS NCI IMPACT CALCULATION - COMPLEX VOLUME-WEIGHTED AGGREGATION
    // ==================================================================================
    //
    // CRITICAL FIX: Resolved 100x magnitude errors in T&S Products NCI calculations
    //
    // ROOT CAUSE: Unit conversion errors between kbbl->MJ, volume aggregation issues,
    // and baseline NCI data corruption from previous calculations
    //
    // SOLUTION:
    // 1. Fixed unit conversions using proper MJ_PER_KBBL conversion factors
    // 2. Implemented volume-weighted carbon intensity aggregation
    // 3. Use preserved baseline NCI values (ORIGINAL_BASELINE_NCI) to prevent corruption
    // 4. Apply proper MTPA to grams conversion (1 trillion factor) in final calculation
    //
    // FORMULA: Combined NCI = (Total Emissions in grams) / (Total Energy in MJ)
    //          NCI Impact = Combined NCI - Baseline NCI
    //
    // VALIDATION: Results now match Excel reference calculations exactly
    // ==================================================================================

    if (isTSProducts) {
      // For T&S Products: use static CAT11 factors
      const contribution = calculateCat11ForTSProducts(
        year,
        index,
        state,
        stepTwoData,
        techSourceOrCommodity
      );
      scope3Cat11Emission += contribution;
    } else if (
      parameters.isCommodityPW &&
      !["Grid", "LNG", NATURAL_GAS].includes(techSourceOrCommodity)
    ) {
      // For Power commodity (non-Grid)
      scope3Cat11Emission += calculateCat11ForPowerNonGrid(
        year,
        index,
        state,
        stepTwoData,
        techSourceOrCommodity
      );
    } else if (parameters.isCommodityPW && techSourceOrCommodity === "Grid") {
      // For Grid
      scope3Cat11Emission += calculateCat11ForGrid(year, index, state, stepTwoData);
    } else if (parameters.isCommodityPg) {
      // For Pipeline Gas
      scope3Cat11Emission += calculateCat11ForPipelineGas(
        year,
        index,
        state,
        stepTwoData,
        parameters
      );
    } else if (parameters.isCommodityRng) {
      // For RNG
      scope3Cat11Emission += calculateCat11ForRNG(year, index, state, stepTwoData, parameters);
    }
  }

  const formatted = isNaN(scope3Cat11Emission)
    ? "-"
    : convertInputToCommaSeparatedWithThreeDecimal(scope3Cat11Emission.toFixed(three));
  rowData[year] = formatted;
};

const updateTotalColumn = (
  rowData: IKpiTableData,
  state: { startYear: number; endYear: number; evp: string }
) => {
  const excludeColumns = getExcludeTotalColumns(state.evp);
  let rowTotal = 0;
  for (let year = state.startYear; year < state.endYear + 1; year++) {
    if (rowData[year] !== undefined) {
      rowTotal += Number(rowData[year].toString().replaceAll(",", ""));
    }
  }
  if (isNaN(rowTotal)) {
    rowData.Total = "-";
  } else {
    rowData.Total = excludeColumns.includes(rowData.KPI as string)
      ? "" // These KPIs should not show totals in the UI
      : convertInputToCommaSeparatedWithThreeDecimal(rowTotal.toFixed(three)); // convert upto 3 decimals while showing in UI
  }
};

const createUpdateFunctions = (
  data: {
    fieldIndex: number;
    rowDataInfo: IKpiTableData;
    equityScopeData: IRecord;
    netAbsEmissionData: IRecord;
    yearWiseKPIrecord: IyearWiseKPIrecord;
    parameters: Iparameters;
    stepTwoData: IStepTwoTableData[];
    firstCol: string;
  },
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  const {
    rowDataInfo,
    equityScopeData,
    netAbsEmissionData,
    yearWiseKPIrecord,
    parameters,
    stepTwoData,
    firstCol,
  } = data;

  // Increment 'fieldIndex' by 1, if technology source is not selected as Gas Fired Generation to match with the conditions.
  if (!parameters.isGasFiredPower) {
    data.fieldIndex += 1;
  }

  return [
    {
      condition: data.fieldIndex === 0 && rowDataInfo.KPI === ASSET_SCOPE1_CI,
      update: (year: number) => {
        updateScope1CmbCrbnInt(
          year,
          rowDataInfo,
          yearWiseKPIrecord.scope1CmbCrbnIntYearWiseObj,
          state,
          dispatch,
          stepTwoData
        );
      },
    },
    {
      condition:
        parameters.isGasFiredPower && data.fieldIndex === 1 && rowDataInfo.KPI === OPERATED_SCOPE,
      update: (year: number) => {
        updateOperatedScope(
          year,
          rowDataInfo,
          yearWiseKPIrecord.operatedScopeYearWiseObj,
          state,
          dispatch,
          stepTwoData
        );
      },
    },
    {
      condition:
        parameters.isGasFiredPower && data.fieldIndex === two && rowDataInfo.KPI === EQUITY_SCOPE,
      update: (year: number) => {
        updateEquityScope(
          year,
          rowDataInfo,
          yearWiseKPIrecord.equityScopeYearWiseObj,
          equityScopeData,
          state,
          dispatch,
          stepTwoData
        );
      },
    },
    {
      // For NET_ABSOLUTE_EMISSIONS: check both original (3) and adjusted (4) fieldIndex
      condition:
        (data.fieldIndex === three && rowDataInfo.KPI === NET_ABSOLUTE_EMISSIONS) ||
        (data.fieldIndex === four && rowDataInfo.KPI === NET_ABSOLUTE_EMISSIONS),
      update: (year: number) => {
        updateNetAbsEmission(
          { yearWiseKPIrecord, year, rowDataInfo, netAbsEmissionData, parameters, firstCol },
          stepTwoData,
          state,
          dispatch
        );
      },
    },
    {
      // For AVG_CARBON_INT: check both original (4) and adjusted (5) fieldIndex
      condition:
        (data.fieldIndex === four && rowDataInfo.KPI === AVG_CARBON_INT) ||
        (data.fieldIndex === five && rowDataInfo.KPI === AVG_CARBON_INT),
      update: (year: number) => {
        updateAvgCarbonIntensity(
          yearWiseKPIrecord.netAbsEmissionYearWise,
          year,
          rowDataInfo,
          parameters,
          state,
          stepTwoData
        );
      },
    },
    // Volume-weighted Average Carbon Intensity calculation
    // For T&S Products: This replaces AVG_CARBON_INT at index four
    // For other EVPs: This is an additional KPI at index five (if shown)
    {
      condition:
        (data.fieldIndex === four && rowDataInfo.KPI === VOLUME_WEIGHTED_AVG_CARBON_INT) ||
        (data.fieldIndex === five && rowDataInfo.KPI === VOLUME_WEIGHTED_AVG_CARBON_INT),
      update: (year: number) => {
        updateVolumeWeightedAvgCI(year, rowDataInfo, parameters, state, stepTwoData);
      },
    },
    // Scope 3 Category 11 Emission calculation
    // For T&S Products: This is at index five (after Volume-weighted CI at index four)
    {
      condition:
        (data.fieldIndex === five && rowDataInfo.KPI === SCOPE3_CAT11_EMISSION) ||
        (data.fieldIndex === six && rowDataInfo.KPI === SCOPE3_CAT11_EMISSION),
      update: (year: number) => {
        updateScope3Cat11Emission(year, rowDataInfo, parameters, state, stepTwoData);
      },
    },
  ];
};

// calculate ci for gas fired power
const calculateCIforGasFiredPower = (
  state: ICalculatorFormState,
  dispatch: Idispatch,
  stepTwoData: IStepTwoTableData[]
) => {
  const isFuelTypeNG = state.technologySource.includes(NATURAL_GAS);
  if (isFuelTypeNG) {
    return calculateCIforGFPfuelTypeNG(state, dispatch, stepTwoData);
  }
  return calculateCIforGFPfuelTypeLNG(state, dispatch, stepTwoData);
};

const getMaxEquityScope = (myState: IKpiContentData) => {
  let maxOfEquityScope = Number.NEGATIVE_INFINITY;
  for (const year in myState.equityScopeDataYearWise) {
    if (year in myState.equityScopeDataYearWise) {
      const value = myState.equityScopeDataYearWise[year];
      if (value > maxOfEquityScope) {
        maxOfEquityScope = value;
      }
    }
  }
  return maxOfEquityScope;
};

const getMaxOfTotalCO2eAndTotalEmission = (myState: IKpiContentData) => {
  // Computing total emissions from netAbsEmsDataYearWise
  let totalEmission = 0;
  let maxOfTotalCO2e = Number.NEGATIVE_INFINITY;
  for (const year in myState.netAbsEmsDataYearWise) {
    if (year in myState.netAbsEmsDataYearWise) {
      const value = myState.netAbsEmsDataYearWise[year];
      if (value > maxOfTotalCO2e) {
        maxOfTotalCO2e = value;
      }
      totalEmission += Number(value);
    }
  }
  // Total emission sum and max calculated
  return { maxOfTotalCO2e, totalEmission };
};

const setCardRowDataValue = (value: number, rowName: string) => {
  if (isNaN(value) || !isFinite(value)) {
    // isFinite() is predefined function that covers both Infinity and -Infinity
    return "-";
  }
  if (
    [
      NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION,
      EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION,
    ].includes(rowName)
  ) {
    return convertInputToCommaSeparatedWithThreeDecimal(value.toFixed(three));
  }
  if (rowName === MARGIN_TO_CARBON_RATIO_AVG) {
    return convertBigIntToDecimal(value);
  }
  return "-";
};

// Helper: Get peak NAE value for T&S Products
const getPeakNAEValueForTS = (netAbsEmsDataYearWise: Record<number, number>): number => {
  let maxKTPA = Number.NEGATIVE_INFINITY;
  for (const year in netAbsEmsDataYearWise) {
    if (netAbsEmsDataYearWise[year] > maxKTPA) {
      maxKTPA = netAbsEmsDataYearWise[year];
    }
  }
  return maxKTPA !== Number.NEGATIVE_INFINITY ? maxKTPA : 0;
};

const updateNetAbsEmsScopePeakAnnualEmission = (
  cardRowData: IKpiCardData[],
  i: number,
  myState: IKpiContentData,
  myDispatch: Idispatch,
  state: { evp: string | null }
) => {
  if (cardFirstColData[i] !== NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION) return;

  const isTSProduct = isTSProductsEVP(state.evp || "");
  const value = isTSProduct
    ? getPeakNAEValueForTS(myState.netAbsEmsDataYearWise)
    : getMaxOfTotalCO2eAndTotalEmission(myState).maxOfTotalCO2e;

  cardRowData[i].Value = setCardRowDataValue(value, NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION);
  cardRowData[i].Unit = MTCO2E;
  myDispatch({ type: "setNetAbsEmsPeak", payload: cardRowData[i].Value });
};

const updateMarginToCarbonRatioAvg = (
  cardRowData: IKpiCardData[],
  i: number,
  dispatch: Idispatch,
  myState: IKpiContentData,
  myDispatch: Idispatch,
  state: { grossMargin: { toNumber: () => number }; evp: string | null }
) => {
  if (cardFirstColData[i] === MARGIN_TO_CARBON_RATIO_AVG) {
    // Skip MCR calculation for SLMT EVP — MCR not applicable for SLMT
    const evpStr = state.evp?.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (evpStr === "slmt") {
      // For SLMT EVP: default to 0.000 and disable MCR calculation/line
      cardRowData[i].Value = "0.000";
      myDispatch({ type: "setMcrAvg", payload: cardRowData[i].Value as string });
      // Set numeric MCR in main state to 0 to avoid infinite or NaN values
      dispatch({ type: "setMcrAverage", payload: 0 });
      return;
    }

    // Starting MCR calculation
    let marginToCarbonRatioAvg = 0;
    const { totalEmission } = getMaxOfTotalCO2eAndTotalEmission(myState);

    // Treat only exact zero, NaN or infinite as invalid denominator.
    // Allow negative totalEmission so MCR can be computed with a negative denominator.
    if (totalEmission === 0 || Number.isNaN(totalEmission) || !Number.isFinite(totalEmission)) {
      cardRowData[i].Value = "-";
      myDispatch({ type: "setMcrAvg", payload: cardRowData[i].Value as string });
      // do not set numeric MCR in main state when totalEmission is invalid
      return;
    }

    marginToCarbonRatioAvg = state.grossMargin.toNumber() / totalEmission;

    // MCR formula computed
    cardRowData[i].Value = setCardRowDataValue(marginToCarbonRatioAvg, MARGIN_TO_CARBON_RATIO_AVG);

    // Final MCR value set
    myDispatch({ type: "setMcrAvg", payload: Number(cardRowData[i].Value).toFixed(three) });
    dispatch({ type: "setMcrAverage", payload: Number(marginToCarbonRatioAvg.toFixed(three)) });
  }
};

const updateEquityScopeCarbonPeakAnnualEmission = (
  cardRowData: IKpiCardData[],
  i: number,
  myState: IKpiContentData,
  myDispatch: Idispatch,
  parameters: Iparameters
) => {
  if (cardFirstColData[i] === EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION) {
    // for gas fired power
    let maxOfEquityScope = 0.0;
    if (parameters.isGasFiredPower) {
      maxOfEquityScope = getMaxEquityScope(myState);
    }
    cardRowData[i].Value = setCardRowDataValue(
      maxOfEquityScope,
      EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION
    );
    myDispatch({ type: "setEquityScope12PeakEmission", payload: cardRowData[i].Value });
  }
};

const copyEquityScope12 = (myState: IKpiContentData, myDispatch: Idispatch) => {
  void navigator.clipboard.writeText(myState.equityScope12PeakEmission.toString());
  myDispatch({
    type: "setCopiedContent",
    payload: EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION,
  });
  myDispatch({
    type: "setIsCopiedToClipboard",
    payload: true,
  });
  const timeoutId = setTimeout(() => {
    myDispatch({
      type: "setIsCopiedToClipboard",
      payload: false,
    });
    // Clear the timeout immediately
    clearTimeout(timeoutId);
  }, thousandfivehundred);
};

const copyNaeScope12 = (myState: IKpiContentData, myDispatch: Idispatch) => {
  void navigator.clipboard.writeText(myState.netAbsEmsPeak.toString());
  myDispatch({
    type: "setCopiedContent",
    payload: NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION,
  });
  myDispatch({
    type: "setIsCopiedToClipboard",
    payload: true,
  });
  const timeoutId = setTimeout(() => {
    myDispatch({
      type: "setIsCopiedToClipboard",
      payload: false,
    });
    // Clear the timeout immediately
    clearTimeout(timeoutId);
  }, thousandfivehundred);
};

const copyMCR = (myState: IKpiContentData, myDispatch: Idispatch) => {
  void navigator.clipboard.writeText(myState.mcrAvg.toString());
  myDispatch({
    type: "setCopiedContent",
    payload: MARGIN_TO_CARBON_RATIO_AVG,
  });
  myDispatch({
    type: "setIsCopiedToClipboard",
    payload: true,
  });
  const timeoutId = setTimeout(() => {
    myDispatch({
      type: "setIsCopiedToClipboard",
      payload: false,
    });
    // Clear the timeout immediately
    clearTimeout(timeoutId);
  }, thousandfivehundred);
};

export const createDealsFromInput = (
  netAbsEmissionYearWise: IRecord,
  commodity: string | string[],
  volumeYearWise: IStepTwoTableData,
  uomVal: string
) => {
  const emissionsData = Object.keys(netAbsEmissionYearWise).map((year) => ({
    YEAR: Number(year),
    CARBON_EMISSIONS: netAbsEmissionYearWise[Number(year)],
  }));
  const volumeData =
    volumeYearWise &&
    Object.entries(volumeYearWise)
      .filter(([year]) => !isNaN(Number(year)))
      .map(([year, quantity]) => {
        return {
          YEAR: Number(year),
          QUANTITY: quantity ? Number(quantity.replace(/,/g, "")) : 0,
        };
      });
  return volumeData?.map(({ YEAR, QUANTITY }) => {
    const carbonEmissions =
      emissionsData.find((emission) => emission.YEAR === YEAR)?.CARBON_EMISSIONS ?? 0;
    return {
      YEAR,
      TRADE_COMMODITY_NAME: commodity,
      QUANTITY_GWH: convertValue(QUANTITY, uomVal, "GWH", commodity) ?? 0,
      CARBON_EMISSIONS: carbonEmissions,
    };
  });
};

// Helper: Calculate NCI Impact for T&S Products
const calculateTSProductsNCIImpact = (
  year: number,
  dealDetailsData: any[],
  netCarbonIntensityData: IGroupNCI[],
  stepTwoTableData: any[] | undefined,
  state: any
): number | null => {
  // Guard: stepTwoTableData is required for T&S Products calculation
  if (!stepTwoTableData) return null;

  // Calculate Deal Energy (MJ) from all products
  let dealEnergyMJ = 0;
  const productKey = state.stepTwoTableFirstCol;

  for (let index = 0; index < stepTwoTableData.length - 1; index++) {
    const commodity = productKey ? stepTwoTableData[index][productKey] : undefined;
    if (commodity === TABLE_ROW_TOTAL_VOLUME) continue;

    const volumeKBBL = parseVolumeFromTableData(stepTwoTableData[index][year]);
    const factor = MJ_PER_KBBL[commodity];
    const energyMJ = factor && volumeKBBL !== undefined ? volumeKBBL * factor : 0;
    dealEnergyMJ += energyMJ;
  }

  // Get Deal NAE (KTPA)
  let dealNAE_KTPA = 0;
  if (state.netAbsEmsDataYearWise && state.netAbsEmsDataYearWise[year]) {
    dealNAE_KTPA = state.netAbsEmsDataYearWise[year];
  } else {
    dealNAE_KTPA = calculateNetAbsEmissionForTSProducts(year, state, stepTwoTableData);
  }
  const dealNAE_MTPA = dealNAE_KTPA / 1000;

  const opData = netCarbonIntensityData.find((item) => item.YEAR === year);
  const baselineNCI = opData?.ORIGINAL_BASELINE_NCI;

  if (!baselineNCI) return null;

  const opGroupNAE_MTPA = opData?.NET_ABSOLUTE_EMISSIONS ?? 0;
  const opGroupEnergy_MJ = opData?.GROUP_VOLUME ?? 0;

  const combinedNAE_MTPA = opGroupNAE_MTPA + dealNAE_MTPA;
  const combinedEnergy_MJ = opGroupEnergy_MJ + dealEnergyMJ;
  const combinedNCI =
    combinedEnergy_MJ > 0 ? (combinedNAE_MTPA * 1_000_000_000_000) / combinedEnergy_MJ : 0;

  return combinedNCI - baselineNCI;
};

// Helper: Calculate NCI Impact for SLMT
const calculateSLMTNCIImpact = (
  year: number,
  netCarbonIntensityData: IGroupNCI[],
  stepTwoTableData: any[] | undefined,
  state: any
): number | null => {
  const groupDataForBaseline = netCarbonIntensityData.find((item) => item.YEAR === year);
  const baselineNCI = groupDataForBaseline?.ORIGINAL_BASELINE_NCI;

  if (!baselineNCI) return null;

  const volumeTBTU = stepTwoTableData?.[0]?.[year];
  const dealEnergyMJ =
    typeof volumeTBTU === "number"
      ? (volumeTBTU / SLMT_CONVERSION_FACTORS.TBTU_HHV_TO_MT_LNG) *
        SLMT_CONVERSION_FACTORS.LNG_KG_TO_MJ_LHV *
        1000000000
      : (Number((volumeTBTU || "").toString().replaceAll(",", "")) /
          SLMT_CONVERSION_FACTORS.TBTU_HHV_TO_MT_LNG) *
        SLMT_CONVERSION_FACTORS.LNG_KG_TO_MJ_LHV *
        1000000000;

  const dealCarbonIntensity = getCarbonIntensityForSLMT(year, state);
  const groupData = netCarbonIntensityData.find((item) => item.YEAR === year);
  const groupEnergyMJ = groupData?.GROUP_VOLUME ?? 0;
  const groupNAE_MTPA = groupData?.NET_ABSOLUTE_EMISSIONS ?? 0;

  const dealNAE_MTPA = (dealEnergyMJ * dealCarbonIntensity) / 1_000_000_000_000;
  const combinedNAE_MTPA = groupNAE_MTPA + dealNAE_MTPA;
  const combinedEnergy_MJ = groupEnergyMJ + dealEnergyMJ;
  const combinedNCI =
    combinedEnergy_MJ > 0 ? (combinedNAE_MTPA * 1_000_000_000_000) / combinedEnergy_MJ : 0;

  return combinedNCI - baselineNCI;
};

// Helper: Calculate NCI for other EVPs (DR Power, Shell Energy)
const calculateOtherEVPNCI = (year: number, netCarbonIntensityData: IGroupNCI[]): string => {
  const nciData = netCarbonIntensityData.find((item: IGroupNCI) => item.YEAR === year);
  let nci = nciData?.CHANGED_REPORTED_GROUP_NCI;

  if (nci === undefined) {
    nci = nciData?.REPORTED_GROUP_NCI;
  }

  let formattedNci = "";
  if (nci !== undefined && nci !== null) {
    const absValue = Math.abs(nci);
    formattedNci = absValue < 0.01 && absValue > 0 ? nci.toFixed(6) : nci.toFixed(3);
  }
  return formattedNci;
};

// Helper: Get carbon credits for a given year
const getCarbonCreditsForYear = (
  stepTwoTableData: any[] | undefined,
  year: number,
  productKey: string | undefined
): number => {
  if (!stepTwoTableData) return 0;
  const carbonCreditsRow = stepTwoTableData.find((row) => {
    const commodity = productKey ? row[productKey] : "";
    return typeof commodity === "string" && commodity.toLowerCase().includes("carbon");
  });
  return carbonCreditsRow ? parseVolumeFromTableData(carbonCreditsRow[year]) : 0;
};

// Helper: Calculate totals for commodities in a year
const calculateCommodityTotals = (
  stepTwoTableData: any[] | undefined,
  year: number,
  productKey: string | undefined,
  productCarbonIntensities: any[]
): { totalNAE_KTPA: number; totalEnergyMJ: number } => {
  let totalNAE_KTPA = 0;
  let totalEnergyMJ = 0;

  if (!stepTwoTableData) return { totalNAE_KTPA, totalEnergyMJ };

  for (let index = 0; index < stepTwoTableData.length - 1; index++) {
    const commodity = productKey ? stepTwoTableData[index][productKey] : undefined;
    if (
      commodity === TABLE_ROW_TOTAL_VOLUME ||
      (typeof commodity === "string" && commodity.toLowerCase().includes("carbon"))
    )
      continue;

    const volumeKBBL = parseVolumeFromTableData(stepTwoTableData[index][year]);
    const mjPerKbbl = MJ_PER_KBBL[commodity] || 0;
    const energyMJ = volumeKBBL * mjPerKbbl;
    const carbonIntensity = getCarbonIntensityForTSProducts(commodity, productCarbonIntensities);

    const naeKTPA = (energyMJ * carbonIntensity) / 1e9;
    totalNAE_KTPA += naeKTPA;
    totalEnergyMJ += energyMJ;
  }

  return { totalNAE_KTPA, totalEnergyMJ };
};

// Helper: Calculate volume-weighted average CI for T&S Products
const calculateVolumeWeightedAvgCI = (
  stepThreeTableData: IKpiModal[],
  stepTwoTableData: any[] | undefined,
  state: any
): void => {
  const volumeWeightedRow = stepThreeTableData.find(
    (row) => row.KPI === VOLUME_WEIGHTED_AVG_CARBON_INT
  );
  if (!volumeWeightedRow) return;

  const productKey = state?.stepTwoTableFirstCol;
  for (let year = state?.startYear || 2026; year <= (state?.endYear || 2029); year++) {
    const carbonCredits = getCarbonCreditsForYear(stepTwoTableData, year, productKey);
    const { totalNAE_KTPA, totalEnergyMJ } = calculateCommodityTotals(
      stepTwoTableData,
      year,
      productKey,
      state?.productCarbonIntensities || []
    );

    const netNAE_KTPA = totalNAE_KTPA - carbonCredits;
    const netNAE_MTPA = netNAE_KTPA / 1000;
    const volumeWeightedAvg = totalEnergyMJ > 0 ? (netNAE_MTPA * 1e12) / totalEnergyMJ : 0;
    volumeWeightedRow[year] = volumeWeightedAvg.toFixed(3);
  }
};

// Helper function to process NCI for a specific year
const processNciForYear = (
  year: number,
  dealsByYear: Record<number, any[]>,
  nciImpactData: IKpiModal,
  netCarbonIntensityData: IGroupNCI[],
  isTSProducts: boolean,
  isSLMT: boolean,
  stepTwoTableData: any[] | undefined,
  state: any
): void => {
  const updateNciDataItem = (nciValue: number): void => {
    const nciDataItem = netCarbonIntensityData.find((item) => item.YEAR === year);
    if (nciDataItem) {
      nciDataItem.CHANGED_REPORTED_GROUP_NCI = Number.parseFloat(nciValue.toFixed(5));
    }
  };

  let nciImpact: number | null = null;

  if (isTSProducts && stepTwoTableData) {
    nciImpact = calculateTSProductsNCIImpact(
      year,
      dealsByYear[year],
      netCarbonIntensityData,
      stepTwoTableData,
      state
    );
    if (nciImpact !== null) {
      nciImpactData[year] = nciImpact.toFixed(5);
      updateNciDataItem(nciImpact);
    }
  } else if (isSLMT && stepTwoTableData) {
    nciImpact = calculateSLMTNCIImpact(year, netCarbonIntensityData, stepTwoTableData, state);
    if (nciImpact !== null) {
      nciImpactData[year] = nciImpact.toFixed(3);
      updateNciDataItem(nciImpact);
    }
  } else {
    nciImpactData[year] = calculateOtherEVPNCI(year, netCarbonIntensityData);
  }
};

// Refactored: Use EVP from state, not product names, for T&S logic
export const getNcidataForStepThreeTable = (
  dealDetailsData: {
    YEAR: number;
    TRADE_COMMODITY_NAME: string | string[];
    QUANTITY_GWH: number;
    CARBON_EMISSIONS: number;
  }[],
  netCarbonIntensityData: IGroupNCI[],
  stepThreeTableData: IKpiModal[],
  evp?: string, // Pass EVP from state
  stepTwoTableData?: any[], // Pass stepTwoTableData for T&S Products
  state?: any // Pass state for product key
) => {
  const nciImpactData: IKpiModal = { KPI: KPI_NCI_IMPACT, Total: "" };

  // ==================================================================================
  // NCI IMPACT CALCULATION ARCHITECTURE - CRITICAL FIXES IMPLEMENTED
  // ==================================================================================
  //
  // PROBLEM RESOLVED: Multiple EVPs (T&S Products, SLMT, DR Power, Shell Energy)
  // had incorrect NCI Impact calculations causing 100x magnitude errors and
  // baseline data corruption when users switched between EVPs.
  //
  // SOLUTION IMPLEMENTED:
  // 1. BASELINE PRESERVATION: Original NCI values preserved in ORIGINAL_BASELINE_NCI
  //    at API load time to prevent data mutation corruption
  // 2. THREE-TIER CALCULATION ARCHITECTURE:
  //    - T&S Products: Complex volume-weighted aggregation with unit conversions
  //    - SLMT: Baseline subtraction (Combined NCI - Baseline NCI)
  //    - DR Power/Shell Energy: Pre-calculated API values
  // 3. DYNAMIC EVP SWITCHING: Users can switch between EVPs in any order without
  //    data corruption or calculation errors
  // ==================================================================================

  // Normalize EVP for comparison
  const normalizedEvp = (evp || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isTSProducts = normalizedEvp === "tsproducts";
  const isSLMT = normalizedEvp === "slmt";

  // Group deals by year
  const dealsByYear: Record<number, typeof dealDetailsData> = {};
  for (const deal of dealDetailsData) {
    if (!dealsByYear[deal.YEAR]) dealsByYear[deal.YEAR] = [];
    dealsByYear[deal.YEAR].push(deal);
  }

  // Process each year with appropriate EVP logic
  for (const yearStr of Object.keys(dealsByYear)) {
    const year = Number(yearStr);
    processNciForYear(
      year,
      dealsByYear,
      nciImpactData,
      netCarbonIntensityData,
      isTSProducts,
      isSLMT,
      stepTwoTableData,
      state
    );
  }

  // Calculate volume-weighted average CI for T&S Products only
  if (isTSProducts && stepTwoTableData) {
    calculateVolumeWeightedAvgCI(stepThreeTableData, stepTwoTableData, state);
  }

  return [...stepThreeTableData, nciImpactData];
};
